import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers, faStar, faBed, faTrophy, faEnvelope, faFilter,
  faPlus, faDownload, faSearch, faXmark,
  faPen, faEye, faCheck, faTrash, faAddressCard,
} from "@fortawesome/free-solid-svg-icons";

import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./GuestsTable.css";
import "./GuestPage.css";
import GuestDetailsModal from "./GuestDetailsModal";
import type { Guest, Booking } from "./types";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

// ── Helpers ──────────────────────────────────────────────────
function getInitials(name: string) {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

function avatarColor(name: string) {
  const colors = ["#1a3a5c","#1a3a5c","#ec4899","#f59e0b","#10b981","#1a3a5c","#ef4444","#14b8a6"];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
}

function exportGuestsCSV(guests: Guest[]) {
  const headers = ["Name","Phone","Email","VIP","LoyaltyPoints","Room","Birthday"];
  const rows = guests.map(g => [
    g.name,
    g.phoneNumber,
    g.email ?? "",
    g.isVIP ? "Yes" : "No",
    g.loyaltyPoints ?? 0,
    g.RoomNumber ?? "",
    g.Birthday ? String(g.Birthday) : "",
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${v ?? ""}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "guests.csv";
  a.click();
  URL.revokeObjectURL(url);
}

// ── Default form state ────────────────────────────────────────
const emptyForm = {
  name: "",
  phoneNumber: "",
  email: "",
  isVIP: false,
  loyaltyPoints: 0,
  Birthday: "",
  RoomNumber: "",
  source: "manual" as "manual" | "booking" | "whatsapp" | "instagram",
  Status: "manual" as "manual" | "lead",
};

type GuestForm = typeof emptyForm;

// ── Add / Edit Modal ──────────────────────────────────────────
interface GuestFormModalProps {
  mode: "add" | "edit";
  initial: GuestForm & { id?: number };
  onClose: () => void;
  onSaved: (guest: Guest) => void;
  onDeleted?: (id: number) => void;
  authHeaders: Record<string, string>;
}

function GuestFormModal({ mode, initial, onClose, onSaved, onDeleted, authHeaders }: GuestFormModalProps) {
  const [form, setForm] = useState<GuestForm>({ ...initial });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");

  const set = (field: keyof GuestForm, value: string | boolean | number) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phoneNumber.trim()) {
      setError("Name and phone number are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const phone = form.phoneNumber.replace(/\D/g, "");
      const body = {
        name: form.name,
        phoneNumber: phone ? Number(phone) : null,
        email: form.email || null,
        isVip: form.isVIP,
        loyaltyPoints: form.loyaltyPoints,
        birthday: form.Birthday || null,
        roomNumber: form.RoomNumber || null,
        source: form.source || "manual",
        Status: form.Status || "manual",
      };
      let res;
      if (mode === "add") {
        res = await axios.post(`${API_BASE}/api/guests`, body, { headers: authHeaders });
      } else {
        res = await axios.put(`${API_BASE}/api/guests/${initial.id}`, body, { headers: authHeaders });
      }
      const raw = res.data.guest ?? res.data;
      const normalized: Guest = {
        id: raw.id,
        name: raw.name,
        phoneNumber: String(raw.phoneNumber ?? ""),
        email: raw.email ?? "",
        isVIP: raw.isVip ?? raw.isVIP ?? false,
        loyaltyPoints: raw.loyaltyPoints ?? 0,
        Birthday: raw.birthday ?? raw.Birthday ?? null,
        WaId: raw.waId ?? raw.WaId ?? null,
        RoomNumber: raw.roomNumber ?? raw.RoomNumber ?? null,
        source: raw.source ?? "manual",
        Status: raw.Status ?? raw.status ?? "manual",
      };
      onSaved(normalized);
    } catch (err: any) {
      console.error("Guest save error:", err?.response?.data ?? err?.message);
      setError(err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? "An error occurred. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!initial.id || !onDeleted) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE}/api/guests/${initial.id}`, { headers: authHeaders });
      onDeleted(initial.id);
    } catch (err: any) {
      console.error("Guest delete error:", err?.response?.data ?? err?.message);
      setError(err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? "Delete failed.");
      setDeleting(false);
    }
  };

  return (
    <div className="gp-form-overlay" onClick={onClose}>
      <div className="gp-form-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="gp-modal-header">
          <div className="gp-modal-avatar">
            {form.name ? form.name.trim()[0].toUpperCase() : (mode === "add" ? "+" : "?")}
          </div>
          <div className="gp-modal-header-info">
            <h2 className="gp-form-title">{mode === "add" ? "Add New Guest" : "Edit Guest"}</h2>
            {mode === "edit" && form.name && <p className="gp-modal-subtitle">{form.name}</p>}
          </div>
          <button className="gp-form-close" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {error && (
          <div className="gp-form-error">{error}</div>
        )}

        {confirmDelete && mode === "edit" && (
          <div className="gp-confirm-box">
            <p>Are you sure you want to delete <strong>{initial.name}</strong>? This action cannot be undone.</p>
            <div className="gp-confirm-actions">
              <button className="gp-submit-btn danger-confirm" onClick={handleDelete} disabled={deleting}>
                {deleting ? "Deleting…" : "Yes, Delete"}
              </button>
              <button className="gp-submit-btn secondary" onClick={() => setConfirmDelete(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="gp-form-section-label">Contact Info</div>
          <div className="gp-form-grid">
            <div className="gp-field full">
              <label>Full Name *</label>
              <input type="text" placeholder="e.g. Ahmed Hassan" value={form.name}
                onChange={e => set("name", e.target.value)} required />
            </div>
            <div className="gp-field">
              <label>Phone Number *</label>
              <input type="tel" placeholder="+20 10 1234 5678" value={form.phoneNumber}
                onChange={e => set("phoneNumber", e.target.value)} required />
            </div>
            <div className="gp-field">
              <label>Email</label>
              <input type="email" placeholder="guest@email.com" value={form.email}
                onChange={e => set("email", e.target.value)} />
            </div>
          </div>

          <div className="gp-form-section-label">Stay Details</div>
          <div className="gp-form-grid">
            <div className="gp-field">
              <label>Room Number</label>
              <input type="text" placeholder="e.g. 201" value={form.RoomNumber}
                onChange={e => set("RoomNumber", e.target.value)} />
            </div>
            <div className="gp-field">
              <label>Birthday</label>
              <input type="date" value={form.Birthday}
                onChange={e => set("Birthday", e.target.value)} />
            </div>
            <div className="gp-field">
              <label>Loyalty Points</label>
              <input type="number" min={0} placeholder="0" value={form.loyaltyPoints}
                onChange={e => set("loyaltyPoints", parseInt(e.target.value) || 0)} />
            </div>
            <div className="gp-field">
              <label>Source</label>
              <select value={form.source} onChange={e => set("source", e.target.value as any)}>
                <option value="manual">Manual</option>
                <option value="booking">Booking.com</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="instagram">Instagram</option>
              </select>
            </div>
            <div className="gp-field">
              <label>Type</label>
              <select value={form.Status} onChange={e => set("Status", e.target.value as any)}>
                <option value="manual">Guest</option>
                <option value="lead">Lead</option>
              </select>
            </div>
          </div>

          <div className="gp-toggle-row">
            <div className="gp-toggle-left">
              <FontAwesomeIcon icon={faStar} style={{ color: "#fbbf24", fontSize: 15 }} />
              <div>
                <span className="gp-toggle-label">VIP Member</span>
                <span className="gp-toggle-sub">Priority service & exclusive perks</span>
              </div>
            </div>
            <label className="gp-toggle">
              <input type="checkbox" checked={form.isVIP} onChange={e => set("isVIP", e.target.checked)} />
              <span className="gp-toggle-slider" />
            </label>
          </div>

          <div className="gp-form-footer">
            <div className="gp-form-footer-left">
              {mode === "edit" && onDeleted && !confirmDelete && (
                <button type="button" className="gp-submit-btn danger" onClick={() => setConfirmDelete(true)}>
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
              )}
            </div>
            <button type="button" className="gp-submit-btn secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="gp-submit-btn primary" disabled={saving}>
              {saving ? "Saving…" : mode === "add" ? "Add Guest" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function GuestsPage() {
  const [guests, setGuests] = useState<Guest[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [filterVIP, setFilterVIP] = useState(false);
  const [typeTab, setTypeTab] = useState<"all" | "guests" | "leads" | "inhouse">("all");

  // Details modal
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [guestBookings, setGuestBookings] = useState<Booking[]>([]);
  const [modalLoading, setModalLoading] = useState(false);

  // Add/Edit modal
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"add" | "edit">("add");
  const [editTarget, setEditTarget] = useState<(GuestForm & { id?: number }) | null>(null);

  // Toast
  const [toast, setToast] = useState("");

  const [theme, setTheme] = useState<"dark" | "light">(() => (localStorage.getItem("guests_theme") as "dark" | "light") || "dark");
  const toggleTheme = () => setTheme(t => { const n = t === "dark" ? "light" : "dark"; localStorage.setItem("guests_theme", n); return n; });

  // Sorting
  const [sortCol, setSortCol] = useState<"name" | "phone" | "loyalty" | "status" | "source">("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const handleSort = (col: typeof sortCol) => {
    setSortDir(prev => sortCol === col ? (prev === "asc" ? "desc" : "asc") : "asc");
    setSortCol(col);
  };

  // Paint the scrollable dashboard area to match guest page theme
  useEffect(() => {
    const main = document.querySelector(".dashboard-main") as HTMLElement | null;
    const content = document.querySelector(".dashboard-content") as HTMLElement | null;
    if (theme === "light") {
      if (main) main.style.background = "#f0f4f8";
      if (content) content.style.background = "#f0f4f8";
    } else {
      if (main) main.style.background = "";
      if (content) content.style.background = "";
    }
    return () => {
      if (main) main.style.background = "";
      if (content) content.style.background = "";
    };
  }, [theme]);

  const token = localStorage.getItem("ai2_token");
  const authHeaders = { Authorization: `Bearer ${token}` };
  const role = localStorage.getItem("ai2_role");
  const canAddGuest = role === "manager" || role === "reception" || role === "admin";

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const fetchGuests = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API_BASE}/api/dashboard/guests`, { headers: authHeaders })
      .then(res => { if (Array.isArray(res.data)) setGuests(res.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchGuests(); }, [fetchGuests]);

  const handleRowClick = async (guest: Guest) => {
    setSelectedGuest(guest);
    setGuestBookings([]);
    setDetailsOpen(true);
    setModalLoading(true);
    try {
      const phone = guest.phoneNumber.replace(/\D/g, "");
      const res = await axios.get(`${API_BASE}/api/dashboard/guests/${phone}/bookings`, { headers: authHeaders });
      setSelectedGuest(res.data.guest ?? guest);
      setGuestBookings(res.data.bookings ?? []);
    } catch (err) {
      console.error("Failed to load bookings:", err);
    } finally {
      setModalLoading(false);
    }
  };

  const openAddModal = () => {
    setFormMode("add");
    setEditTarget({ ...emptyForm });
    setFormOpen(true);
  };

  const openEditModal = (guest: Guest, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setDetailsOpen(false);
    setFormMode("edit");
    setEditTarget({
      id: guest.id,
      name: guest.name ?? "",
      phoneNumber: guest.phoneNumber ?? "",
      email: guest.email ?? "",
      isVIP: guest.isVIP ?? false,
      loyaltyPoints: guest.loyaltyPoints ?? 0,
      Birthday: guest.Birthday ? String(guest.Birthday).slice(0, 10) : "",
      RoomNumber: guest.RoomNumber ?? "",
      source: (guest.source as any) ?? "manual",
      Status: (guest.Status as any) ?? "manual",
    });
    setFormOpen(true);
  };

  const handleGuestSaved = (saved: Guest) => {
    setFormOpen(false);
    if (formMode === "add") {
      setGuests(prev => [saved, ...prev]);
      showToast("Guest added successfully.");
    } else {
      setGuests(prev => prev.map(g => (g.id === saved.id ? saved : g)));
      showToast("Guest updated successfully.");
    }
  };

  const handleGuestDeleted = (id: number) => {
    setFormOpen(false);
    setDetailsOpen(false);
    setGuests(prev => prev.filter(g => g.id !== id));
    showToast("Guest deleted.");
  };

  const handleDeleteFromDetails = (guestId: number) => {
    axios
      .delete(`${API_BASE}/api/guests/${guestId}`, { headers: authHeaders })
      .then(() => {
        setDetailsOpen(false);
        setGuests(prev => prev.filter(g => g.id !== guestId));
        showToast("Guest deleted.");
      })
      .catch(err => {
        console.error("Delete failed:", err?.response?.data ?? err?.message);
        showToast(err?.response?.data?.message ?? err?.response?.data?.error ?? "Delete failed.");
      });
  };

  const filtered = guests.filter(g => {
    const q = searchQuery.toLowerCase();
    const matchSearch =
      (g.name?.toLowerCase() || "").includes(q) ||
      (g.phoneNumber || "").includes(q) ||
      (g.email?.toLowerCase() || "").includes(q);
    const matchVIP = !filterVIP || g.isVIP;
    const gStatus = g.Status || "manual";
    const matchTab =
      typeTab === "all"     ? true :
      typeTab === "leads"   ? gStatus === "lead" :
      typeTab === "inhouse" ? !!g.RoomNumber :
      /* guests */            gStatus !== "lead";
    return matchSearch && matchVIP && matchTab;
  }).sort((a, b) => {
    let av: string | number = "";
    let bv: string | number = "";
    if (sortCol === "name")    { av = (a.name || "").toLowerCase(); bv = (b.name || "").toLowerCase(); }
    if (sortCol === "phone")   { av = a.phoneNumber || ""; bv = b.phoneNumber || ""; }
    if (sortCol === "loyalty") { av = a.loyaltyPoints ?? 0; bv = b.loyaltyPoints ?? 0; }
    if (sortCol === "status")  { av = (a.isVIP ? "vip" : a.Status || "").toLowerCase(); bv = (b.isVIP ? "vip" : b.Status || "").toLowerCase(); }
    if (sortCol === "source")  { av = (a.source || "").toLowerCase(); bv = (b.source || "").toLowerCase(); }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const vipCount = guests.filter(g => g.isVIP).length;
  const inHouseCount = guests.filter(g => g.RoomNumber).length;
  const withEmailCount = guests.filter(g => g.email).length;
  const leadsCount = guests.filter(g => (g.Status || "manual") === "lead").length;
  const guestsCount = guests.filter(g => (g.Status || "manual") !== "lead").length;
  const avgLoyalty = guests.length
    ? Math.round(guests.reduce((s, g) => s + (g.loyaltyPoints ?? 0), 0) / guests.length)
    : 0;

  return (
    <DashboardLayout>
      <div className={`guests-page${theme === "light" ? " light-mode" : ""}`}>

        {/* PAGE HEADER */}
        <div className="gp-header">
          <div className="gp-header-left">
            <h1>Guests</h1>
            <p>
              {loading ? "Loading…" : `${guests.length} total · ${vipCount} VIP`}
            </p>
          </div>
          <div className="gp-header-actions">
            <button className="gp-theme-btn" onClick={toggleTheme} title={theme === "dark" ? "Light mode" : "Dark mode"}>
              {theme === "dark" ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                  <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                  <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>
            {canAddGuest && (
              <button className="gp-btn primary" onClick={openAddModal}>
                <FontAwesomeIcon icon={faPlus} /> Add Guest
              </button>
            )}
            <button className="gp-btn secondary" onClick={() => exportGuestsCSV(filtered)}>
              <FontAwesomeIcon icon={faDownload} /> Export CSV
            </button>
          </div>
        </div>

        {/* ANALYTICS STRIP */}
        <div className="gp-analytics">
          <div className="gp-stat-card">
            <div className="gp-stat-icon" style={{ background: "rgba(201,168,76,0.12)", color: "#1a3a5c" }}>
              <FontAwesomeIcon icon={faUsers} />
            </div>
            <span className="gp-stat-value">{loading ? "—" : guests.length}</span>
            <span className="gp-stat-label">Total Guests</span>
          </div>
          <div className="gp-stat-card">
            <div className="gp-stat-icon" style={{ background: "rgba(251,191,36,0.12)", color: "#fbbf24" }}>
              <FontAwesomeIcon icon={faStar} />
            </div>
            <span className="gp-stat-value" style={{ color: "#fbbf24" }}>{loading ? "—" : vipCount}</span>
            <span className="gp-stat-label">VIP Members</span>
          </div>
          <div className="gp-stat-card">
            <div className="gp-stat-icon" style={{ background: "rgba(16,185,129,0.12)", color: "#34d399" }}>
              <FontAwesomeIcon icon={faBed} />
            </div>
            <span className="gp-stat-value" style={{ color: "#34d399" }}>{loading ? "—" : inHouseCount}</span>
            <span className="gp-stat-label">In-House</span>
          </div>
          <div className="gp-stat-card">
            <div className="gp-stat-icon" style={{ background: "rgba(201,168,76,0.12)", color: "#c9a84c" }}>
              <FontAwesomeIcon icon={faTrophy} />
            </div>
            <span className="gp-stat-value" style={{ color: "#c9a84c" }}>{loading ? "—" : avgLoyalty}</span>
            <span className="gp-stat-label">Avg Loyalty Pts</span>
          </div>
          <div className="gp-stat-card">
            <div className="gp-stat-icon" style={{ background: "rgba(6,182,212,0.12)", color: "#22d3ee" }}>
              <FontAwesomeIcon icon={faEnvelope} />
            </div>
            <span className="gp-stat-value" style={{ color: "#22d3ee" }}>{loading ? "—" : withEmailCount}</span>
            <span className="gp-stat-label">With Email</span>
          </div>
          <div className="gp-stat-card">
            <div className="gp-stat-icon" style={{ background: "rgba(14,34,64,0.10)", color: "#1a3a5c" }}>
              <FontAwesomeIcon icon={faFilter} />
            </div>
            <span className="gp-stat-value" style={{ color: "#1a3a5c" }}>{loading ? "—" : filtered.length}</span>
            <span className="gp-stat-label">Showing</span>
          </div>
        </div>

        {/* TYPE TABS */}
        <div className="gp-type-tabs">
          {([
            { key: "all",     label: "All",      count: guests.length },
            { key: "guests",  label: "Guests",   count: guestsCount },
            { key: "leads",   label: "Leads",    count: leadsCount },
            { key: "inhouse", label: "In-House", count: inHouseCount },
          ] as const).map(t => (
            <button
              key={t.key}
              className={`gp-type-tab ${typeTab === t.key ? "active" : ""}`}
              onClick={() => setTypeTab(t.key)}
            >
              {t.label}
              <span className="gp-type-tab-count">{loading ? "…" : t.count}</span>
            </button>
          ))}
        </div>

        {/* CONTROLS */}
        <div className="gp-controls">
          <div className="gp-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="gp-search-icon" />
            <input
              className="gp-search-input"
              type="text"
              placeholder="Search by name, phone, email…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="gp-search-clear" onClick={() => setSearchQuery("")}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            )}
          </div>
          <button
            className={`gp-filter-btn ${filterVIP ? "active" : ""}`}
            onClick={() => setFilterVIP(v => !v)}
          >
            <FontAwesomeIcon icon={faStar} /> VIP Only
          </button>
        </div>

        {/* TABLE */}
        <GuestsTableInline
          guests={filtered}
          loading={loading}
          onRowClick={handleRowClick}
          onEditClick={openEditModal}
          role={role}
          sortCol={sortCol}
          sortDir={sortDir}
          onSort={handleSort}
        />

        {/* DETAILS MODAL */}
        <GuestDetailsModal
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          guest={selectedGuest}
          bookings={guestBookings}
          loading={modalLoading}
          onEdit={openEditModal}
          onDelete={handleDeleteFromDetails}
        />

        {/* ADD / EDIT FORM MODAL */}
        {formOpen && editTarget && (
          <GuestFormModal
            mode={formMode}
            initial={editTarget}
            onClose={() => setFormOpen(false)}
            onSaved={handleGuestSaved}
            onDeleted={formMode === "edit" ? handleGuestDeleted : undefined}
            authHeaders={authHeaders}
          />
        )}

        {/* TOAST */}
        {toast && (
          <div className="gp-toast">
            <FontAwesomeIcon icon={faCheck} />
            {toast}
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}

const PAGE_SIZE = 20;

// ── Sort indicator ────────────────────────────────────────────
function SortIcon({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
  return (
    <span className={`gp-sort-icon${active ? " active" : ""}`}>
      {active ? (dir === "asc" ? " ↑" : " ↓") : " ↕"}
    </span>
  );
}

// ── Inline Table Component ────────────────────────────────────
function GuestsTableInline({
  guests,
  loading,
  onRowClick,
  onEditClick,
  role,
  sortCol,
  sortDir,
  onSort,
}: {
  guests: Guest[];
  loading: boolean;
  onRowClick: (g: Guest) => void;
  onEditClick: (g: Guest, e: React.MouseEvent) => void;
  role: string | null;
  sortCol: string;
  sortDir: "asc" | "desc";
  onSort: (col: "name" | "phone" | "loyalty" | "status" | "source") => void;
}) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(guests.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = guests.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Reset to page 1 when guest list changes (search/filter)
  useEffect(() => { setPage(1); }, [guests.length]);

  if (loading) return (
    <div className="guests-table-wrapper">
      <div className="guests-skeleton">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="skeleton-row">
            <div className="skeleton-avatar" />
            <div className="skeleton-lines">
              <div className="skeleton-line w60" />
              <div className="skeleton-line w40" />
            </div>
            <div className="skeleton-line w30" />
            <div className="skeleton-line w50" />
            <div className="skeleton-line w20" />
          </div>
        ))}
      </div>
    </div>
  );

  if (guests.length === 0) return (
    <div className="guests-table-wrapper">
      <div className="guests-empty">
        <div className="guests-empty-icon">
          <FontAwesomeIcon icon={faUsers} style={{ fontSize: 48, opacity: 0.3 }} />
        </div>
        <p className="guests-empty-title">No guests found</p>
        <p className="guests-empty-sub">Try adjusting your search or filters</p>
      </div>
    </div>
  );

  return (
    <div className="guests-table-wrapper">
      <table className="guests-table">
        <thead>
          <tr>
            <th className="gp-th-sortable" onClick={() => onSort("name")}>
              Guest <SortIcon active={sortCol === "name"} dir={sortDir} />
            </th>
            <th className="gp-th-sortable" onClick={() => onSort("phone")}>
              Contact <SortIcon active={sortCol === "phone"} dir={sortDir} />
            </th>
            <th className="gp-th-sortable" onClick={() => onSort("source")}>
              Source <SortIcon active={sortCol === "source"} dir={sortDir} />
            </th>
            <th className="gp-th-sortable" onClick={() => onSort("status")}>
              Status <SortIcon active={sortCol === "status"} dir={sortDir} />
            </th>
            <th className="gp-th-sortable" onClick={() => onSort("loyalty")}>
              Loyalty <SortIcon active={sortCol === "loyalty"} dir={sortDir} />
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paged.map(g => {
            const name = g.name || "Unknown";
            const color = avatarColor(name);
            return (
              <tr key={g.id ?? g.phoneNumber} className="guest-row" onClick={() => onRowClick(g)}>
                <td>
                  <div className="guest-cell">
                    <div className="guest-avatar-circle" style={{ background: color }}>
                      {getInitials(name)}
                    </div>
                    <div className="guest-cell-info">
                      <span className="guest-name">{name}</span>
                      {g.email && <span className="contact-email" style={{ fontSize: 11 }}>{g.email}</span>}
                    </div>
                  </div>
                </td>
                <td>
                  <div className="contact-cell">
                    <span className="contact-phone">{g.phoneNumber || "—"}</span>
                    {g.email && <span className="contact-email">{g.email}</span>}
                  </div>
                </td>
                <td>
                  <span className={`source-badge source-${g.source ?? "manual"}`}>
                    {g.source ?? "manual"}
                  </span>
                </td>
                <td>
                  {g.isVIP
                    ? <span className="badge vip"><FontAwesomeIcon icon={faStar} /> VIP</span>
                    : (g.Status === "lead")
                    ? <span className="badge lead">Lead</span>
                    : g.RoomNumber
                    ? <span className="badge inhouse">In-House</span>
                    : <span className="badge regular">Guest</span>}
                </td>
                <td>
                  <div className="loyalty-cell">
                    <div className="loyalty-bar-track">
                      <div
                        className="loyalty-bar-fill"
                        style={{ width: `${Math.min((g.loyaltyPoints ?? 0) / 10, 100)}%` }}
                      />
                    </div>
                    <span className="loyalty-pts">{g.loyaltyPoints ?? 0} pts</span>
                  </div>
                </td>
                <td>
                  <div className="gp-row-actions">
                    <button
                      className="gp-action-btn edit"
                      onClick={e => onEditClick(g, e)}
                      title="Edit guest"
                    >
                      <FontAwesomeIcon icon={faPen} /> Edit
                    </button>
                    <button
                      className="gp-action-btn view"
                      onClick={e => { e.stopPropagation(); onRowClick(g); }}
                      title="View details"
                    >
                      <FontAwesomeIcon icon={faEye} /> View
                    </button>
                    <button
                      className="gp-action-btn profile"
                      onClick={e => {
                        e.stopPropagation();
                        const phone = g.phoneNumber.replace(/\D/g, "");
                        const base = role === "reception" ? "/dashboard/reception" : "/dashboard/manager";
                        navigate(`${base}/guests/${phone}`);
                      }}
                      title="Full profile"
                    >
                      <FontAwesomeIcon icon={faAddressCard} /> Profile
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="guests-table-footer">
        <span>{guests.length} guest{guests.length !== 1 ? "s" : ""}</span>
        {totalPages > 1 && (
          <div className="gp-pagination">
            <button
              className="gp-page-btn"
              disabled={safePage === 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >‹ Prev</button>
            <span className="gp-page-info">Page {safePage} of {totalPages}</span>
            <button
              className="gp-page-btn"
              disabled={safePage === totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            >Next ›</button>
          </div>
        )}
      </div>
    </div>
  );
}
